/**
 * Japanese Translations (日本語)
 */

import type { Translations } from '../types';

export const ja: Translations = {
  language: 'ja',
  languageName: '日本語',

  common: {
    demo: 'デモ',
    privacy: 'プライバシー',
    terms: '利用規約',
  },

  privacy: {
    title: 'プライバシーポリシー',
    whatWeStore: '保存するもの',
    whatWeStoreDesc:
      '年齢推定のために顔の形状を一時的に処理します。確認完了後、画像や生体テンプレートは保存されません。',
    whatWeDont: '保存しないもの',
    whatWeDontDesc:
      '写真、動画、生体識別子は保存しません。顔データはブラウザ内で処理され、即座に破棄されます。',
    whyWeUseIt: '使用する理由',
    whyWeUseItDesc:
      '年齢推定は、個人書類やアカウントを必要とせずに年齢制限規制を遵守するのに役立ちます。',
    readFull: '完全な',
    privacyPolicyLink: 'プライバシーポリシー',
  },

  theme: {
    system: 'システム',
    light: 'ライト',
    dark: 'ダーク',
  },

  face: {
    noFace: '顔が検出されません',
    tooFar: '顔が遠すぎます',
    tooClose: '顔が近すぎます',
    offCenter: '顔が中央にありません',
    poorLighting: '照明が不十分です',
    good: '分析中、そのままで...',
    holdStill: '分析中、そのままで...',
    holdStillCountdown: 'そのまま... {count}',
    processing: '処理中...',
    looking: '顔が検出されません',
    centerFace: '顔が中央にありません',
    moveCloser: '顔が遠すぎます',
    moveBack: '顔が近すぎます',
    keepInPosition: '位置を維持',
    perfect: '完璧',
    centerInFrame: '顔をフレーム内に収めてください',
    outsideOval: '顔が楕円の外にあります',
  },

  liveness: {
    blink: {
      title: 'まばたきしてください',
      hint: 'ゆっくり1回まばたきしてください',
    },
    smile: {
      title: '笑顔を見せてください',
      hint: '自然な笑顔を見せてください',
    },
    lookLeft: {
      title: '左を向いてください',
      hint: 'ゆっくり頭を左に向けてください',
    },
    lookRight: {
      title: '右を向いてください',
      hint: 'ゆっくり頭を右に向けてください',
    },
    openMouth: {
      title: '口を開けてください',
      hint: '口を大きく開けてください',
    },
    lookUp: {
      title: '上を見てください',
      hint: '頭を上に傾けてください',
    },
    lookDown: {
      title: '下を見てください',
      hint: '頭を下に傾けてください',
    },
    raiseBrows: {
      title: '眉を上げてください',
      hint: '眉を上げてください',
    },
    improveLighting: {
      title: '照明を改善してください',
      hint: '続行するには明るい場所に移動してください',
    },
  },

  document: {
    selectType: '書類の種類を選択してください',
    captureFront: '書類の表面を撮影してください',
    captureBack: '書類の裏面を撮影してください',
    processing: '書類を処理中...',
    tips: {
      goodLighting: '十分な照明を確保してください',
      flatSurface: '平らな面に書類を置いてください',
      allCorners: 'すべての角が見えるようにしてください',
      noGlare: '反射や影を避けてください',
    },
    types: {
      passport: 'パスポート',
      idCard: '身分証明書',
      driversLicense: '運転免許証',
    },
  },

  ui: {
    continue: '続ける',
    next: '次へ',
    retry: '再試行',
    startOver: '最初から',
    cancel: 'キャンセル',
    success: '年齢チェック合格',
    failure: '年齢チェック不合格',
    loading: '読み込み中...',
    error: 'エラーが発生しました',
    permissionRequired: '許可が必要です',
    cameraPermission: 'カメラへのアクセスが必要です',
    goBack: '戻る',
    switchCamera: 'カメラを切り替え',
    completeVerification: '完了',
    continueToId: 'ID確認へ進む',
  },

  errors: {
    cameraNotFound: 'このデバイスにカメラが見つかりません',
    cameraPermissionDenied: 'カメラの許可が拒否されました',
    cameraInUse: 'カメラは別のアプリで使用中です',
    cameraNotSupported: 'カメラは必要な設定をサポートしていません',
    cameraSecurityError: 'カメラにはHTTPS接続が必要です',
    networkError: 'ネットワークエラー。接続を確認してください。',
    timeout: 'リクエストがタイムアウトしました。再試行してください。',
    invalidApiKey: '無効なAPIキー',
    sessionExpired: 'セッションが期限切れです。最初からやり直してください。',
    verificationFailed: 'チェックに失敗しました。もう一度お試しください。',
    unknownError: '予期しないエラーが発生しました',
    cameraAccessDeniedDetailed: '続行するにはカメラへのアクセスを許可してください。',
    cameraNotFoundDetailed: 'カメラが検出されません。カメラを接続して再試行してください。',
    cameraInUseDetailed: 'カメラは別のアプリで使用中です。そのアプリを閉じて再試行してください。',
    cameraOverconstrained:
      'カメラは要求された解像度をサポートしていません。デフォルト設定で試行中...',
    networkTimeout: 'リクエストに時間がかかりすぎました。接続を確認して再試行してください。',
    networkOffline: 'オフラインのようです。インターネット接続を確認してください。',
    apiGenericError: '問題が発生しました。再試行してください。',
    faceNotDetected: '顔をフレーム内に配置してください。',
    livenessFailed: '生体検知に失敗しました。再試行してください。',
    documentCaptureFailed: '書類を撮影できませんでした。十分な照明を確保して再試行してください。',
    initializationFailed: '読み込みに失敗しました。ページを更新して再試行してください。',
    modelLoadFailed:
      '必要なコンポーネントの読み込みに失敗しました。ページを更新して再試行してください。',
    unexpectedError: '予期しないエラーが発生しました。再試行してください。',
    configError: '設定エラーです。サポートにお問い合わせください。',
  },

  screens: {
    welcome: {
      title: '18歳以上 年齢確認',
      subtitle: '匿名年齢確認',
      startButton: '開始',
      secureNote: '安全・暗号化',
      ageTechBy: '年齢技術提供 AppBouncer',
      waitingCamera: 'カメラアクセスを待っています',
      allowCamera: 'カメラアクセスを許可',
      noBiometric: '生体データは保存されません',
      loading: '読み込み中...',
      loadingRequirements: '要件を読み込み中...',
    },
    permissions: {
      title: '読み込み中 ...',
      description: 'プロンプトが表示されたらカメラアクセスを許可してください',
      allowButton: 'カメラアクセスを許可',
      privacyNote: 'データは暗号化され安全に保護されています',
      goodLighting: '良好な照明！',
      goodLightingHint: '顔が十分に照らされていることを確認してください',
      getReady: '準備してください',
      removeCoverings: 'マスクを外してください',
      removeGlasses: 'メガネを外してください',
    },
    facePosition: {
      title: '顔を配置してください',
      hint: '顔を中央に',
      step: 'ステップ',
      stepOf: 'ステップ {current}/{total}',
      lookingForFace: '顔を探しています...',
    },
    livenessCheck: {
      title: '生体検知',
      followInstructions: '指示に従ってください',
      preparing: '準備中...',
      complete: '生体認証完了！',
      actionAutoDetected: 'アクションは自動的に検出されます',
      failed: '生体検知に失敗しました',
      failedDescription:
        '生体確認ができませんでした。十分な照明があることを確認し、カメラを真正面から見て、画面の指示に従ってください。',
      retryButton: '生体認証を再試行',
      restartButton: 'やり直す',
    },
    ageEstimation: {
      title: '動かないでください',
      analyzing: '分析中...',
      analyzingProgress: '顔を分析中 {current}/{total}...',
      estimatedAge: '推定年齢',
      holdStill: '動かないでください',
      verified: '年齢確認済み',
      verificationUncertain: '年齢不確定',
      estimationComplete: '年齢チェック完了',
      confirmedAge: '{minAge}歳以上を確認',
      couldNotConfirm: '{minAge}歳以上を確認できませんでした',
      ageConfirmed: '年齢確認',
      aboveMinimumAge: '年齢要件を満たしています',
      belowMinimumAge: '最低年齢未満',
      documentRequired: '書類が必要です',
      uploadDocumentMessage: '年齢を確認するために有効な身分証明書をアップロードしてください',
      idHint: '身分証明書、パスポート、または運転免許証',
      confidenceLevels: '年齢閾値別の信頼度',
      confidence: '信頼度',
      years: '歳',
      continueToId: 'ID確認へ進む',
      demoComplete: 'デモモード完了。本番環境ではリダイレクトされます。',
      improveLighting: '照明を改善してください',
      improveLightingHint: '正確な結果を得るために明るい場所に移動してください',
    },
    documentSelect: {
      title: '書類の種類を選択',
      description: 'スキャンする身分証明書の種類を選択してください',
      idCard: '国民身分証明書',
      idCardHint: '政府発行のID',
      passport: 'パスポート',
      passportHint: '写真ページのみ',
      driversLicense: '運転免許証',
      driversLicenseHint: '有効な運転免許証',
    },
    captureMode: {
      captureFrontSide: '表面を撮影',
      captureBackSide: '裏面を撮影',
      chooseMethod: '書類の撮影方法を選択',
      useCamera: 'カメラを使用',
      useCameraHint: '書類の写真を撮影',
      uploadFile: 'ファイルをアップロード',
      uploadFileHint: 'デバイスから選択',
    },
    documentCamera: {
      frontSide: '表面',
      backSide: '裏面',
      positionInFrame: '書類をフレーム内に配置',
      scanFront: '書類の表面をスキャン',
      scanBack: '書類の裏面をスキャン',
      keepFlat: '書類を平らに置き、十分な照明を確保してください',
      capturePhoto: '写真を撮影',
    },
    documentPreview: {
      frontPreview: '表面プレビュー',
      backPreview: '裏面プレビュー',
      lookingGood: '良い感じです！',
      checkDetails: 'すべての詳細がはっきり見えることを確認してください',
      imageQuality: '画像品質',
      qualityGood: '良好',
      usePhoto: 'この写真を使用',
      retakePhoto: '再撮影',
    },
    documentCapture: {
      captureFront: '表面を撮影',
      captureBack: '裏面を撮影',
      alignDocument: '書類をフレーム内に揃えてください',
      captureButton: '撮影',
      retakeButton: '再撮影',
      confirmButton: '確認',
      frontSide: '表面',
      backSide: '裏面',
      positionDocument: '書類をフレーム内に配置してください',
      alignWithFrame: '書類をフレームに合わせてください',
      positionFrontId: 'IDの表面を配置',
      positionBackId: 'IDの裏面を配置',
      scanFrontId: 'IDの表面をスキャン',
      scanBackId: 'IDの裏面をスキャン',
      positionWithinFrame: 'フレーム内に表面を配置',
      flipAndScan: 'IDを裏返して裏面をスキャン',
      goodLight: '良好な照明',
      flatSurface: '平らな面',
      frontCaptured: '表面を撮影しました',
      captureIdCard: '身分証明書を撮影',
      capturePassport: 'パスポートを撮影',
      captureDriversLicense: '運転免許証を撮影',
    },
    processing: {
      title: '処理中',
      description: '処理中です。お待ちください',
      analyzingDocument: '書類を分析中...',
      almostDone: 'もうすぐ完了...',
      faceVerified: '顔が確認されました',
      livenessConfirmed: '生体確認されました',
      processingDocuments: '書類を処理中...',
    },
    success: {
      title: '年齢確認完了',
      description: '年齢・生体確認完了',
      redirecting: 'リダイレクト中...',
      summary: '概要',
      faceMatch: '顔照合',
      liveness: '生体確認',
      age: '年齢',
      idDocument: '身分証明書',
      pass: '合格',
      fail: '不合格',
      valid: '有効',
      notApplicable: '該当なし',
      done: '完了',
      feedbackTitle: 'ご体験を評価してください',
      feedbackPlaceholder: '改善点をお聞かせください...',
      skip: 'スキップ',
      submit: '送信',
    },
    failure: {
      title: '年齢チェック不合格',
      description: 'チェックを完了できませんでした。もう一度お試しください。',
      tryAgainButton: '再試行',
      leaveButton: '終了',
      attemptsLeft: '残り {{count}} 回',
      noAttemptsLeft: '試行回数の上限に達しました',
    },
    error: {
      title: '設定が必要です',
      description: 'URLに必要なパラメータがありません。サイト管理者にお問い合わせください。',
      missingParams: '不足しているパラメータ',
      requiredParams: '必須パラメータ',
      technicalDetails: '技術的詳細',
      urlMustInclude: 'URLには次のクエリパラメータを含める必要があります:',
    },
    underage: {
      title: '年齢要件を満たしていません',
      description: 'このサービスにアクセスするには{{minAge}}歳以上である必要があります。',
    },
    consent: {
      title: '年齢チェックの改善にご協力ください',
      description:
        'AppBouncerが年齢確認とフォールバック認証を改善するために、少量の匿名化された使用データ（カメラキャプチャなど）を収集することを許可してください。お客様のデータは第三者に販売または提供されることはありません。',
      allowButton: '許可する',
    },
    documentConsent: {
      title: '書類が必要です',
      description:
        'チェックを完了するため、公的書類で年齢を確認する必要があります。身分証明書のアップロードに同意しますか？',
      allowButton: '書類で続行',
      declineButton: '拒否する',
      privacyNote: '書類は安全に処理され、チェック後に削除されます。',
    },
    selfDeclaration: {
      title: '生年月日',
      description: '続行するには生年月日を入力してください',
      day: '日',
      month: '月',
      year: '年',
      yourAge: 'あなたの年齢：',
      years: '歳',
      confirmButton: '続ける',
      months: {
        january: '1月',
        february: '2月',
        march: '3月',
        april: '4月',
        may: '5月',
        june: '6月',
        july: '7月',
        august: '8月',
        september: '9月',
        october: '10月',
        november: '11月',
        december: '12月',
      },
    },
  },

  lighting: {
    warning: '照明の問題',
    adjust: '照明を調整してください',
    lowLight: '低照度を検出しました',
    tooBright: '明るすぎます',
    poorContrast: 'コントラスト不足',
    improveLighting: 'より良い照明を探してください',
    moveToLighter: '明るい場所に移動してください',
    videoNotReady: 'ビデオの準備ができていません',
  },

  steps: {
    faceDetection: '顔検出',
    liveness: '生体検知',
    age: '年齢チェック',
    documentScan: '書類スキャン',
    selfDeclaration: '自己申告',
  },

  status: {
    cameraStarted: 'カメラが起動しました',
    cameraReady: 'カメラ準備完了',
    cameraAccessDenied: 'カメラアクセスが拒否されました',
    cameraError: 'カメラエラー',
    cameraNotAvailable: 'カメラが利用できません',
    noFaceDetected: '顔が検出されません',
    livenessVerified: '生体確認完了',
    livenessCheckFailed: '生体検知に失敗しました',
    verifyingAge: '年齢を確認中...',
    processingDocument: '書類を処理中...',
    documentProcessed: '書類の処理が完了しました',
    verificationCancelled: 'チェックがキャンセルされました',
    improveLightingForAge: '正確な結果のために照明を改善してください',
    getReady: '準備してください',
    preparingLiveness: '生体検知を準備中...',
    uploadingSelfie: 'セルフィーをアップロード中...',
    preparingUpload: 'アップロードを準備中...',
    uploadingFront: '表面の書類をアップロード中...',
    uploadingBack: '裏面の書類をアップロード中...',
    processingDocuments: '書類を処理中...',
    documentSubmissionFailed: '書類の送信に失敗しました。再試行してください。',
    selectJpegOrPng: 'JPEGまたはPNG画像を選択してください',
    fileTooLarge: 'ファイルが大きすぎます。最大5MBまでです。',
    errorReadingFile: 'ファイルの読み取りエラー',
    frontDocumentRequired: '表面の書類画像が必要です',
    verificationFailedClose: 'チェックに失敗しました。リダイレクトされます。',
    verificationCompleteClose: 'チェック完了！リダイレクトされます。',
    analyzingFace: '顔を分析中...',
  },
};
